import { z } from 'zod';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * OTONOM OPERATÖR — SÖZLEŞME (29 Eyl 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Arka planda sürekli çalışan AI'nın üç kalıcı nesnesi:
 *
 *   · NOT    (`OperatorNote`) — AI'nın kendi hafızası: öğrendiği, kullanıcının
 *            tercihi, sonraki koşuda kontrol edeceği hipotez.
 *   · GÖREV  (`OperatorTask`) — AI'nın KENDİ kurduğu zamanlayıcı: "24 saat
 *            sonra bu kampanyaya bak", "her sabah 9'da harcamayı kontrol et".
 *   · KOŞU   (`OperatorRun`) — bir uyanış: neden uyandı, ne okudu, ne yaptı,
 *            ne önerdi, kaça mal oldu.
 *
 * Sohbet ajanı da (kullanıcı "yarın sabah bak" dediğinde) aynı görev ve not
 * servislerini kullanıyor — iki ayrı hafıza OLMAZ.
 *
 * Plan: /var/www/vhosts/mentorivo.com/OTONOM-OPERATOR-PLANI.md §1.C
 */

// ═══════════════════════════════════════════════════════════════════════════
// Notlar
// ═══════════════════════════════════════════════════════════════════════════

export const OPERATOR_NOTE_KINDS = [
  /// Doğrulanmış gerçek ("Marka kelimeleri kampanyası ROAS 6+, dokunma").
  'FACT',
  /// Deneyden çıkan ders ("Bu hesapta hafta sonu CPA %30 yüksek").
  'LEARNING',
  /// Kullanıcının söylediği tercih ("Günlük toplam 2000 TL'yi geçme").
  'PREFERENCE',
  /// Test edilecek fikir ("Video kreatif görselden iyi olabilir — 3 gün izle").
  'HYPOTHESIS',
  /// Yapılacak iş (görevden farkı: zamanı yok).
  'TODO',
  /// Dikkat ("Ödeme yöntemi 3 gün içinde sona eriyor").
  'WARNING',
] as const;
export type OperatorNoteKind = (typeof OPERATOR_NOTE_KINDS)[number];

export const OPERATOR_NOTE_LIMITS = {
  contentMax: 2_000,
  /** Çalışma alanı başına açık (arşivlenmemiş) not tavanı; aşılınca en eski düşük önemli arşivlenir. */
  activeMax: 300,
  /** Koşu istemine giden not özetinin karakter tavanı. */
  digestMaxChars: 12_000,
} as const;

export const operatorRememberSchema = z.object({
  kind: z.enum(OPERATOR_NOTE_KINDS),
  content: z.string().trim().min(3).max(OPERATOR_NOTE_LIMITS.contentMax),
  /** Notun ilgili olduğu platform (yoksa çalışma alanı geneli). */
  platform: z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'TWITTER']).optional(),
  /** İlgili varlık: platform kimliği ya da Mentorivo UUID'si. */
  entityRef: z.string().trim().min(1).max(300).optional(),
  entityLevel: z.enum(['ACCOUNT', 'CAMPAIGN', 'ADSET', 'AD', 'KEYWORD', 'CREATIVE', 'PRODUCT']).optional(),
  /** 1 (düşük) – 5 (kritik). Özet önem sırasıyla kesilir. */
  importance: z.number().int().min(1).max(5).default(3),
  /** Kendiliğinden arşivlenme (gün). Hipotez ve uyarılar için önerilir. */
  expiresInDays: z.number().int().min(1).max(365).optional(),
});
export type OperatorRememberInput = z.input<typeof operatorRememberSchema>;

export const operatorForgetSchema = z.object({
  noteId: z.uuid(),
  /** Neden artık geçerli değil — denetim ve sonraki koşular için. */
  reason: z.string().trim().max(500).optional(),
});

export const operatorNoteListSchema = z.object({
  platform: z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'TWITTER']).optional(),
  entityRef: z.string().trim().max(300).optional(),
  kind: z.enum(OPERATOR_NOTE_KINDS).optional(),
  limit: z.number().int().min(1).max(100).default(50),
});

export interface OperatorNoteRow {
  id: string;
  kind: OperatorNoteKind;
  content: string;
  platform: string | null;
  entityLevel: string | null;
  entityRef: string | null;
  importance: number;
  pinned: boolean;
  createdBy: 'agent' | 'user';
  sourceRunId: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Görevler
// ═══════════════════════════════════════════════════════════════════════════

export const OPERATOR_TASK_KINDS = [
  /// Tek seferlik takip ("24 saat sonra optimize et").
  'FOLLOW_UP',
  /// Tekrarlayan kontrol ("her gün 09:00").
  'RECURRING',
  /// Koşul izleme — her devriyede değerlendirilir ("CPA 150'yi geçerse durdur").
  'WATCH',
  /// Sistemin kurduğu devriye (çalışma alanı başına tek).
  'PATROL',
] as const;
export type OperatorTaskKind = (typeof OPERATOR_TASK_KINDS)[number];

export const OPERATOR_TASK_STATUSES = ['PENDING', 'RUNNING', 'DONE', 'CANCELLED', 'FAILED'] as const;
export type OperatorTaskStatus = (typeof OPERATOR_TASK_STATUSES)[number];

export const OPERATOR_TASK_LIMITS = {
  /** En kısa gecikme — daha sık bakmak platform kotası yakar. */
  minDelayMinutes: 5,
  /** En uzak vade. */
  maxDelayDays: 90,
  /** Tekrarlayan görevin en kısa aralığı. */
  minIntervalMinutes: 30,
  /** Çalışma alanı başına açık görev tavanı. */
  activeMax: 100,
} as const;

export const operatorScheduleSchema = z
  .object({
    kind: z.enum(['FOLLOW_UP', 'RECURRING', 'WATCH']).default('FOLLOW_UP'),
    /** Kısa başlık — arayüz listesinde görünür. */
    title: z.string().trim().min(3).max(160),
    /**
     * Uyandığında kendine vereceği talimat — o anki bağlamı taşıyacak kadar
     * açık yazılmalı ("Kampanya X 29 Eyl'de 150 TL/gün ile açıldı; CPA'ya bak,
     * 3 dönüşümün altındaysa en kötü reklamı durdur").
     */
    instruction: z.string().trim().min(10).max(4_000),
    /** Göreli gecikme (dakika) — `at` ile birlikte verilemez. */
    inMinutes: z.number().int().min(OPERATOR_TASK_LIMITS.minDelayMinutes).max(OPERATOR_TASK_LIMITS.maxDelayDays * 24 * 60).optional(),
    /** Mutlak an (ISO 8601). */
    at: z.iso.datetime({ offset: true }).optional(),
    /** RECURRING için tekrar aralığı (dakika). */
    repeatEveryMinutes: z.number().int().min(OPERATOR_TASK_LIMITS.minIntervalMinutes).max(30 * 24 * 60).optional(),
    platform: z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'TWITTER']).optional(),
    /** İlgili varlıklar (platform kimliği ya da Mentorivo UUID'si). */
    entityRefs: z.array(z.string().trim().min(1).max(300)).max(20).optional(),
  })
  .refine((v) => (v.inMinutes === undefined) !== (v.at === undefined), {
    message: 'Tam olarak biri verilmeli: inMinutes ya da at',
  })
  .refine((v) => v.kind !== 'RECURRING' || v.repeatEveryMinutes !== undefined, {
    message: 'RECURRING görev repeatEveryMinutes ister',
  });
export type OperatorScheduleInput = z.input<typeof operatorScheduleSchema>;

export const operatorCancelTaskSchema = z.object({
  taskId: z.uuid(),
  reason: z.string().trim().max(500).optional(),
});

export const operatorTaskListSchema = z.object({
  status: z.enum(OPERATOR_TASK_STATUSES).optional(),
  limit: z.number().int().min(1).max(100).default(50),
});

export interface OperatorTaskRow {
  id: string;
  kind: OperatorTaskKind;
  status: OperatorTaskStatus;
  /** Düz başlık (İngilizce ya da kullanıcının/modelin yazdığı dil). */
  title: string;
  /**
   * SİSTEMİN kurduğu görevde başlığın çeviri anahtarı (`operator.systemTask.*`)
   * ve parametreleri — ekran kendi dilinde kuruyor (Faz 5, 29 Eyl 2026).
   * Kullanıcının ya da modelin yazdığı görevde `null`: başlık zaten o dilde.
   */
  titleKey: OperatorSystemTaskKey | null;
  titleParams: Record<string, string> | null;
  /** Modele giden talimat — sistem görevlerinde İNGİLİZCE kalır. */
  instruction: string;
  dueAt: string;
  intervalMinutes: number | null;
  platform: string | null;
  entityRefs: string[];
  createdBy: 'agent' | 'user' | 'system';
  runCount: number;
  lastRunAt: string | null;
  lastRunId: string | null;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Koşular
// ═══════════════════════════════════════════════════════════════════════════

export const OPERATOR_RUN_TRIGGERS = ['TASK', 'PATROL', 'EVENT', 'USER'] as const;
export type OperatorRunTrigger = (typeof OPERATOR_RUN_TRIGGERS)[number];

export const OPERATOR_RUN_STATUSES = ['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED', 'SKIPPED'] as const;
export type OperatorRunStatus = (typeof OPERATOR_RUN_STATUSES)[number];

/**
 * Operatörü uyandıran olaylar. Yayıncısı olmayan olay listeye EKLENMEZ
 * (sözleşme-vs-gerçeklik dersi).
 *
 * Yayıncılar (29 Eyl 2026, Faz 2):
 *   · `campaign.launched`  → worker `operator/operator-watch.service.ts`:
 *     denetim defterini tarıyor (`campaign.status_changed` → ACTIVE,
 *     `platform_op.applied` etkisi NEW_SPEND, onaylı `resume` aksiyonu).
 *     Koşu AÇMIYOR, iki takip görevi kuruyor (+2 sa teslimat, +24 sa optimizasyon).
 *   · `ad.disapproved`     → aynı servis: `DISAPPROVED` duruma düşen reklamlar.
 *   · `performance.alert`  → worker `performance/performance-scan.service.ts`
 *     (günlük tarama; `conversions_up` HARİÇ — iyi haber alarm değil).
 *   · `action.failed`      → API `operator/operator-events.listener.ts`
 *     (`EVENT.WORKFLOW_ACTION_FAILED`).
 *
 * ⛔ `delivery.stalled` ÇIKARILDI: yayıncısı yoktu. "Yayındaki kampanya hiç
 * gösterim almadı" sorusunu yayın takibinin +2 sa görevi soruyor.
 */
export const OPERATOR_EVENTS = [
  /// Kampanya yayına alındı — 2 sa teslimat + 24 sa optimizasyon görevi kurulur.
  'campaign.launched',
  /// Reklam platform incelemesinden reddedildi.
  'ad.disapproved',
  /// Performans taraması alarmı (CPA↑, harcama sıçraması, ROAS↓, dönüşüm↓).
  'performance.alert',
  /// Bir platform yazması başarısız oldu.
  'action.failed',
  /**
   * Lead formundan yeni potansiyel müşteri geldi (Faz 5, 29 Eyl 2026 — lead
   * ÇEKME ürün sahibi kararıyla geri geldi). Yayıncı worker
   * `operator/operator-watch.service.ts` → `scanLeads`: lead amaçlı
   * kampanyaların DÖNÜŞÜM sayısındaki artışı izliyor (`metrics_daily`) —
   * platforma ek istek YOK, kişisel veri HİÇ okunmuyor/saklanmıyor (imleç
   * Redis'te yalnız sayı). Koşu lead'lerin kendisini `<platform>.lead.list`
   * okuma operasyonuyla okur.
   */
  'lead.received',
  /**
   * Reklama yeni yorum geldi (Faz 7b, 30 Eyl 2026). Yayıncı API
   * `comments/comment-watch.service.ts`: tazelik zamanlayıcısı (worker)
   * yorum izlemesi açık hesapları `LIVE_CADENCE.comments*` ritmiyle
   * `QUEUE.COMMENT_WATCH`e atıyor; API `<platform>.comment.list` okuma
   * operasyonuyla (`PlatformOpsService`) yeni yorumları buluyor. Olay satırı
   * YORUM METNİ ve YAZAR TAŞIMAZ — platform, reklam, yorum kimlikleri ve
   * sayı; koşu metni aynı liste operasyonuyla okur (sonuç
   * `untrustedUserContent` çerçevesinden geçer). Bir yoklamanın bütün
   * reklamları TEK `fireEvent` çağrısında (çalışma alanı başına toplu);
   * mevcut olay debounce'u geçerli.
   */
  'ad.comment.received',
] as const;
export type OperatorEvent = (typeof OPERATOR_EVENTS)[number];

export const OPERATOR_RUN_LIMITS = {
  /** Koşu başına en fazla araç çağrısı. */
  maxToolCalls: 40,
  /** Olay koşuları arasında çalışma alanı başına en kısa aralık (sn) — kritik olay hariç. */
  eventDebounceSeconds: 15 * 60,
  /** Plan başına günlük koşu tavanı (-1 sınırsız). */
  dailyRunsByPlan: { TRIAL: 6, STARTER: 12, GROWTH: 48, PRO: 144, ENTERPRISE: -1 } as Record<string, number>,
} as const;

export interface OperatorRunRow {
  id: string;
  trigger: OperatorRunTrigger;
  status: OperatorRunStatus;
  /** İngilizce düz sebep — model ve eski istemciler. Ekran `reasonKind`i okur. */
  reason: string;
  /**
   * Yapılandırılmış sebep (Faz 5, 29 Eyl 2026) — ekran çeviri anahtarı
   * `operator.runReason.<kind>` + `reasonParams` ile kuruyor. Eski satırlarda
   * `null` → `reason` metni gösterilir.
   */
  reasonKind: OperatorRunReasonKind | null;
  reasonParams: OperatorRunReasonParams | null;
  taskId: string | null;
  eventType: string | null;
  summary: string | null;
  proposalIds: string[];
  appliedCount: number;
  pendingCount: number;
  notesWritten: number;
  tasksScheduled: number;
  inputTokens: number;
  outputTokens: number;
  model: string | null;
  /**
   * Hata — `storedError` biçimi (`[KOD] ayrıntı`, `stored-error.ts`): ekran
   * `useStoredErrorText` ile KODU kendi dilinde çeviriyor, ayrıntı (platformun
   * ham cümlesi, sanitize) olduğu gibi. Faz 5 öncesi satırlar düz metin
   * ("KOD: Türkçe ayrıntı") — kodsuz parça olduğu gibi basılıyor.
   */
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Koşu sebebi — yapılandırılmış (Faz 5, 29 Eyl 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Koşunun NEDEN uyandığı — ekran `operator.runReason.<kind>` anahtarıyla
 * kendi dilinde kuruyor. `reason` (düz İngilizce) modele ve eski istemcilere
 * kalıyor.
 *
 * Neden `trigger` yetmiyor: `USER` tetiği "şimdi bak" düğmesi (serbest
 * yönergesiz) ile yönergeli isteği ayırmıyor; `TASK` tetiği görevin başlığını
 * taşımıyor. Sebep bir tetik + onun parametreleri.
 */
export const OPERATOR_RUN_REASON_KINDS = [
  /** Rutin devriye. */
  'PATROL',
  /** Vadesi gelen görev — `{ taskId, title, titleKey?, titleParams? }`. */
  'TASK',
  /** Olay(lar) — `{ events: OperatorEvent[] }`. */
  'EVENT',
  /** Kullanıcının yönergeli "şimdi bak" isteği — `{ instruction }`. */
  'USER_REQUEST',
  /** Kullanıcının yönergesiz "şimdi bak" düğmesi. */
  'USER_REVIEW',
] as const;
export type OperatorRunReasonKind = (typeof OPERATOR_RUN_REASON_KINDS)[number];

export interface OperatorRunReasonParams {
  taskId?: string;
  /** Görevin düz başlığı (sistem görevinde İngilizce). */
  title?: string;
  /** Sistem görevinde başlığın çeviri anahtarı + parametreleri. */
  titleKey?: OperatorSystemTaskKey;
  titleParams?: Record<string, string>;
  /** EVENT: birleşen olay türleri (tekil, geliş sırası). */
  events?: string[];
  /** USER_REQUEST: kullanıcının yönergesi (tek satır, ≤ 300). */
  instruction?: string;
}

export interface OperatorRunReason {
  /** Düz İngilizce — `operator_runs.reason`. */
  text: string;
  kind: OperatorRunReasonKind;
  params: OperatorRunReasonParams;
}

/**
 * Koşu sebebini kurar — API ve worker AYNI fonksiyonu kullanıyor (iki taraf
 * da koşu satırı açıyor; metin ayrışırsa ekran iki farklı cümle gösterirdi).
 */
export function operatorRunReason(
  input:
    | { kind: 'PATROL' }
    | { kind: 'TASK'; task: { id: string; title: string; dedupeKey?: string | null } }
    | { kind: 'EVENT'; events: readonly string[] }
    | { kind: 'USER'; instruction: string | null },
): OperatorRunReason {
  switch (input.kind) {
    case 'PATROL':
      return { text: 'Patrol', kind: 'PATROL', params: {} };
    case 'TASK': {
      const system = operatorSystemTaskTitle({
        title: input.task.title,
        dedupeKey: input.task.dedupeKey ?? null,
      });
      return {
        text: `Task: ${input.task.title}`.slice(0, 200),
        kind: 'TASK',
        params: {
          taskId: input.task.id,
          title: input.task.title.slice(0, 160),
          ...(system ? { titleKey: system.key, titleParams: system.params } : {}),
        },
      };
    }
    case 'EVENT': {
      const events = [...new Set(input.events)];
      return { text: `Event: ${events.join(', ')}`.slice(0, 200), kind: 'EVENT', params: { events } };
    }
    case 'USER': {
      const instruction = input.instruction?.replace(/\s+/g, ' ').trim() ?? '';
      if (!instruction) return { text: 'User request: review now', kind: 'USER_REVIEW', params: {} };
      const short = instruction.length > 140 ? `${instruction.slice(0, 139)}…` : instruction;
      return {
        text: `User request: ${short}`,
        kind: 'USER_REQUEST',
        params: { instruction: instruction.slice(0, 300) },
      };
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Sistem görevlerinin başlıkları — çeviri anahtarı + parametre (Faz 5)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Sistemin kurduğu görevlerin başlık anahtarları — ekran
 * `operator.systemTask.<anahtar>` ile çeviriyor.
 *
 * Neden kolon değil: `operator_tasks` şeması bu fazda değişmiyor (tek
 * migration yalnız `operator_runs`). Sistem görevini `dedupeKey` zaten
 * tanımlıyor (`patrol`, `launch:<ref>:2h|24h`) ve başlığı BİZ yazıyoruz —
 * anahtar ve parametre satırdan DETERMİNİSTİK türüyor. Talimat (`instruction`)
 * modele gidiyor ve İNGİLİZCE kalıyor.
 */
export const OPERATOR_SYSTEM_TASK_KEYS = ['patrol', 'launchDelivery', 'launchOptimization'] as const;
export type OperatorSystemTaskKey = (typeof OPERATOR_SYSTEM_TASK_KEYS)[number];

/** Sistem görevlerinin İngilizce başlıkları — worker/API AYNI metni yazıyor. */
export const OPERATOR_SYSTEM_TASK_TITLES = {
  patrol: 'Patrol',
  launchDelivery: (name: string) => `Delivery check: ${name}`.slice(0, 160),
  launchOptimization: (name: string) => `24h optimization: ${name}`.slice(0, 160),
} as const;

/**
 * Görev satırından sistem başlığının anahtarı + parametreleri; sistem
 * görevi değilse `null`. Anahtar `dedupeKey`ten (sistemin koyduğu), ad
 * başlığın bilinen önekinden sonrası.
 */
export function operatorSystemTaskTitle(task: {
  title: string;
  dedupeKey: string | null;
}): { key: OperatorSystemTaskKey; params: Record<string, string> } | null {
  const key = task.dedupeKey ?? '';
  if (key === OPERATOR_PATROL_KEY) return { key: 'patrol', params: {} };
  const launch = /^launch:.+:(2h|24h)$/.exec(key);
  if (!launch) return null;
  const which: OperatorSystemTaskKey = launch[1] === '2h' ? 'launchDelivery' : 'launchOptimization';
  const prefix = which === 'launchDelivery' ? 'Delivery check: ' : '24h optimization: ';
  const name = task.title.startsWith(prefix) ? task.title.slice(prefix.length) : task.title;
  return { key: which, params: { name } };
}

// ═══════════════════════════════════════════════════════════════════════════
// Canlı konu — `operator` (Faz 5, 29 Eyl 2026)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `LiveEvent.ref` biçimi `operator` konusunda: `run:<id>`, `task:<id>`,
 * `note:<id>`. Ekran hangi listeyi tazeleyeceğini önekten anlıyor (koşu
 * ayrıntısı açıksa yalnız o koşuyu yeniden okuyor). Olay VERİ TAŞIMAZ.
 *
 * Yayın anları: koşu QUEUED (açıldı / olay birleşti), RUNNING, bitti
 * (SUCCEEDED/FAILED/SKIPPED); görev kuruldu/iptal/yeniden vadelendi/koşu
 * sonrası güncellendi; not yazıldı/arşivlendi.
 */
export function operatorLiveRef(kind: 'run' | 'task' | 'note', id: string): string {
  return `${kind}:${id}`;
}

/** `operatorLiveRef`in tersi — tanınmayan biçimde `null`. */
export function parseOperatorLiveRef(
  ref: string | null,
): { kind: 'run' | 'task' | 'note'; id: string } | null {
  const match = /^(run|task|note):(.+)$/.exec(ref ?? '');
  return match ? { kind: match[1] as 'run' | 'task' | 'note', id: match[2]! } : null;
}

// ═══════════════════════════════════════════════════════════════════════════
// Ayarlar (workspaces sütunları — `getAutonomyPolicy`)
// ═══════════════════════════════════════════════════════════════════════════

export const operatorSettingsSchema = z
  .object({
    operatorEnabled: z.boolean(),
    operatorPatrolMinutes: z.number().int().min(30).max(24 * 60),
    autoLaunchCampaigns: z.boolean(),
    maxNewCampaignsPerDay: z.number().int().min(0).max(50),
    maxDailySpendIncrease: z.number().positive().max(100_000_000).nullable(),
    allowIrreversibleOps: z.boolean(),
    /* Hassas işlem otonomisi (Faz 7, 30 Eyl 2026) — `AUTO_SENSITIVE_SETTINGS`.
       🔴 YALNIZ SAHİP değiştirir (`OWNER_ONLY_AUTONOMY_SETTINGS`). */
    autoSensitiveAccess: z.boolean(),
    autoSensitiveDestination: z.boolean(),
    autoSensitiveDataSharing: z.boolean(),
    autoSensitiveLegal: z.boolean(),
    autoSensitivePublishing: z.boolean(),
    autoSensitiveFunds: z.boolean(),
    /** Günlük otomatik para hareketi tavanı (çalışma alanı para birimi, ANA birim); `null` = FUNDS asla otomatik değil. */
    maxFundsMovedPerDay: z.number().positive().max(100_000_000).nullable(),
    /**
     * Dalga 5 — insan yokken (operatör/kural) günlük onaysız kredi tavanı;
     * yalnız tam otonomi + AUTOPILOT'ta etkili. 0 = asla (varsayılan).
     * 🔴 YALNIZ SAHİP (`OWNER_ONLY_AUTONOMY_SETTINGS`).
     */
    maxCreativeCreditsPerDay: z.number().int().min(0).max(100_000),
    /**
     * TAM OTONOMİ (Faz 7c, 30 Eyl 2026) — AUTOPILOT ile birlikte AI hiçbir işte
     * insan onayı beklemez (`isFullAutonomy`, `decideOp`). 🔴 AÇMAK yalnız
     * SAHİP + güncel onay sürümü (`fullAutonomyConsentVersion`,
     * `FULL_AUTONOMY_CONSENT_VERSION`); kapatmak `workspace.update` yetkisi
     * olan herkes (acil fren — daraltma).
     */
    fullAutonomy: z.boolean(),
  })
  .partial();
export type OperatorSettingsInput = z.infer<typeof operatorSettingsSchema>;

/**
 * Hassasiyet → çalışma alanı ayar alanı (Faz 7). Sıra `OP_SENSITIVITIES` ile
 * aynı; arayüz bu sırayla listeler. Tek eşleme burada: politika okuyucusu
 * (`WorkspaceService.getAutonomyPolicy`), ayar ekranı ve sahip kilidi bunu
 * OKUR — ayrı liste yazılmaz (hafıza: sözleşme-vs-gerçeklik). Bütünlüğü
 * `platform-ops.policy.spec.ts` denetliyor (her hassasiyetin tek satırı var).
 */
export const AUTO_SENSITIVE_SETTINGS = [
  { sensitivity: 'ACCESS', key: 'autoSensitiveAccess' },
  { sensitivity: 'DESTINATION', key: 'autoSensitiveDestination' },
  { sensitivity: 'DATA_SHARING', key: 'autoSensitiveDataSharing' },
  { sensitivity: 'LEGAL', key: 'autoSensitiveLegal' },
  { sensitivity: 'PUBLISHING', key: 'autoSensitivePublishing' },
  { sensitivity: 'FUNDS', key: 'autoSensitiveFunds' },
] as const satisfies readonly {
  sensitivity: import('./platform-ops/types.js').OpSensitivity;
  key: keyof OperatorSettingsInput;
}[];
export type AutoSensitiveSettingKey = (typeof AUTO_SENSITIVE_SETTINGS)[number]['key'];

/**
 * 🔴 Yalnız çalışma alanı SAHİBİNİN (OWNER) değiştirebildiği otonomi alanları
 * (Faz 7). Kullanıcı kararı: müşterinin erişimini, varış adresini ya da
 * PARASINI AI, müşteri açmadan taşımamalı. `PATCH /workspaces/current/settings`
 * ADMIN'e açık; bu alanlardan biri DEĞİŞİYORSA servis OWNER istiyor. Ajan ve
 * otomasyon araçları bu alanları hiçbir yoldan yazmamalı.
 */
export const OWNER_ONLY_AUTONOMY_SETTINGS: readonly (keyof OperatorSettingsInput)[] = [
  ...AUTO_SENSITIVE_SETTINGS.map((row) => row.key),
  'maxFundsMovedPerDay',
  /* Dalga 5 — müşterinin KREDİSİ: AI'ın onaysız harcama tavanını yalnız sahip açar. */
  'maxCreativeCreditsPerDay',
  /* Faz 7c — AÇMAK sahip işi. KAPATMAK (true → false) istisna: acil fren,
     `WorkspaceService.updateSettings` ADMIN'e de izin veriyor. Listede durması
     "hiçbir ajan/otomasyon aracı bu alanı yazamaz" sözleşmesinin parçası. */
  'fullAutonomy',
];

/**
 * TAM OTONOMİ ONAY METNİNİN SÜRÜMÜ (Faz 7c) — `PayService.consentSnapshot`
 * deseni: sahip açarken ekrandaki metni kabul ediyor ve kabul, SÜRÜMÜYLE
 * denetim kaydına yazılıyor (`workspace.updated` → `after.fullAutonomyConsent`).
 *
 * 🔴 Onay diyaloğunun METNİ (`dashboard.settings.workspace.autopilot.fullAutonomy.consent.*`)
 * anlamca değişirse bu sürüm de DEĞİŞMELİ: eski sürümle gelen açma isteği
 * reddediliyor (`FULL_AUTONOMY_CONSENT_REQUIRED`) — sahip neyi kabul ettiğini
 * görmeden yeni anlamı kabul etmiş sayılmasın.
 */
export const FULL_AUTONOMY_CONSENT_VERSION = '2026-09-30.1';

/**
 * Onay diyaloğunun saydığı yetkiler — sıra ekrandaki sıra. Metinler
 * i18n'de (`…fullAutonomy.consent.powers.<anahtar>`); liste burada ki denetim
 * kaydı ve ekran aynı kümeyi anlatsın.
 */
export const FULL_AUTONOMY_POWERS = [
  'moveMoney',
  'acceptTerms',
  'grantAccess',
  'uploadCustomerData',
  'deleteThings',
  'launchAndSpend',
] as const;
export type FullAutonomyPower = (typeof FULL_AUTONOMY_POWERS)[number];

/**
 * Tam otonomide de İNSANDA kalanlar (ürün sahibinin önceki kararı: "güvenlik
 * ayarları insanda"). Hiçbir sohbet/operatör/otomasyon aracı bunları yapamaz;
 * diyalog ve istemler bu listeyi söylüyor.
 */
export const FULL_AUTONOMY_HUMAN_ONLY = [
  'apiKeys',
  'ipRestriction',
  'twoFactor',
  'ownershipTransfer',
  'workspaceDeletion',
  'paymentsAndSubscription',
  'autonomySettings',
] as const;

/** GET/PATCH `/workspaces/current/settings` yanıtındaki operatör alanları (hepsi dolu). */
export type OperatorSettings = Required<OperatorSettingsInput>;

// ═══════════════════════════════════════════════════════════════════════════
// HTTP uçları — sorgu ve gövde şemaları (Faz 2, 29 Eyl 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Araç şemaları (`operatorNoteListSchema` …) modelden SAYI alıyor; HTTP sorgu
// dizesi ise METİN getiriyor ("50"). Aynı şemayı uçta kullanmak her listeyi
// 400'e düşürürdü — uç şemaları `z.coerce` ile ayrı.

export const operatorNoteQuerySchema = z.object({
  platform: z.enum(['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'TWITTER']).optional(),
  entityRef: z.string().trim().max(300).optional(),
  kind: z.enum(OPERATOR_NOTE_KINDS).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type OperatorNoteQuery = z.infer<typeof operatorNoteQuerySchema>;

export const operatorTaskQuerySchema = z.object({
  status: z.enum(OPERATOR_TASK_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type OperatorTaskQuery = z.infer<typeof operatorTaskQuerySchema>;

export const operatorRunQuerySchema = z.object({
  status: z.enum(OPERATOR_RUN_STATUSES).optional(),
  trigger: z.enum(OPERATOR_RUN_TRIGGERS).optional(),
  /** İmleç: bir önceki sayfanın `nextBefore`u (koşunun `createdAt`i). */
  before: z.iso.datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});
export type OperatorRunQuery = z.infer<typeof operatorRunQuerySchema>;

export interface OperatorRunPage {
  items: OperatorRunRow[];
  /** Sonraki sayfa için `before`; `null` = son sayfa. */
  nextBefore: string | null;
}

/** `POST /operator/run-now` — kullanıcının "şimdi bak" düğmesi. */
export const operatorRunNowSchema = z.object({
  /** İsteğe bağlı odak ("Meta'daki yeni kampanyaya bak"). Yoksa genel devriye. */
  instruction: z.string().trim().min(3).max(4_000).optional(),
});
export type OperatorRunNowInput = z.infer<typeof operatorRunNowSchema>;

/** Birleştirilmiş olay kaydı (`operator_runs.events`). */
export interface OperatorEventEntry {
  type: OperatorEvent;
  at: string;
  /** İnsan/model okur tek satır — ham platform yanıtı DEĞİL. */
  detail: string;
  /** İlgili varlık (kampanya/reklam kimliği). */
  ref?: string | null;
}

export const OPERATOR_EVENT_LIMITS = {
  /** Tek koşuda birleştirilen en fazla olay — fazlası sayılır, listelenmez. */
  maxEventsPerRun: 20,
  detailMax: 500,
} as const;

export interface OperatorRunDetail extends OperatorRunRow {
  /** Modele giden ilk kullanıcı mesajı. */
  prompt: string | null;
  events: OperatorEventEntry[];
  toolCalls: { name: string; ok: boolean; summary: string }[];
  transcript: unknown[];
  stopReason: string | null;
  cacheReadTokens: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Zamanlama kuralları — API ve worker AYNI hesabı yapsın diye saf fonksiyonlar
// ═══════════════════════════════════════════════════════════════════════════
//
// Worker `apps/api`yi import edemiyor; görevi kuran (API: sohbet aracı, uç)
// ile claim eden (worker) aynı vade/tavan kuralını iki ayrı yerde yazsaydı
// biri güncellenip diğeri unutulurdu.

export const OPERATOR_SCHEDULE = {
  /** Worker'ın vadesi gelen görev / bekleyen olay taraması (sn). */
  tickSeconds: 30,
  /** Tek turda claim edilen en fazla görev (kiracılar toplamı). */
  claimBatch: 25,
  /** Operatör açılınca ilk devriyenin gecikmesi (dk). */
  firstPatrolDelayMinutes: 5,
  /** RUNNING'de bu kadar kalan koşu yarıda kalmış sayılır (dk). */
  runTimeoutMinutes: 30,
  /** Kuyruğa atılmış ama bu kadar süredir başlamamış koşu yeniden atılır (dk). */
  requeueAfterMinutes: 10,
  /** Tek seferlik görev bu kadar art arda başarısız olursa FAILED. */
  maxFollowUpFailures: 3,
  /** Başarısız tek seferlik görevin yeniden deneme gecikmesi (dk). */
  retryDelayMinutes: 15,
  /** WATCH görevine aralık verilmemişse (dk). */
  defaultWatchIntervalMinutes: 240,
  /** `at` ile verilen vadede geçmişe tolerans (sn) — saat kayması. */
  pastToleranceSeconds: 120,
} as const;

/** Devriye görevinin deterministik anahtarı — çalışma alanı başına TEK. */
export const OPERATOR_PATROL_KEY = 'patrol';

/** Bekleyen (henüz kuyruğa atılmamış) olay koşusunun birleştirme anahtarı. */
export function operatorEventPendingKey(workspaceId: string): string {
  return `event:${workspaceId}`;
}

/**
 * Operatörün koşabildiği abonelik durumları. `EXPIRED`/`CANCELED` alanda
 * veri okunur ama arka plan AI'sı (LLM maliyeti + otomatik yazma) durur.
 */
export const OPERATOR_ACTIVE_SUBSCRIPTION_STATUSES: readonly string[] = [
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'GRACE',
];

/**
 * Plan başına günlük koşu tavanı. `0` = koşamaz (plansız / süresi dolmuş),
 * `-1` = sınırsız. Kataloğu olmayan bir plan adı `0` — bilinmeyeni sınırsız
 * saymak yeni bir planı sessizce bedava yapardı.
 */
export function operatorDailyRunCap(
  subscription: { plan: string; status: string } | null | undefined,
): number {
  if (!subscription) return 0;
  if (!OPERATOR_ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status)) return 0;
  return OPERATOR_RUN_LIMITS.dailyRunsByPlan[subscription.plan] ?? 0;
}

/** Son 24 saatteki koşu sayısı tavana dayandı mı (`-1` sınırsız). */
export function operatorCapReached(cap: number, runsLast24h: number): boolean {
  return cap !== -1 && runsLast24h >= cap;
}

export type OperatorDueAtResult =
  | { ok: true; dueAt: Date }
  | { ok: false; code: 'IN_PAST' | 'TOO_FAR'; message: string };

/**
 * `operatorScheduleSchema` girdisinden vade.
 *
 * `at` en kısa gecikmenin altındaysa (şimdi + 5 dk) en kısa gecikmeye
 * ÇEKİLİYOR, reddedilmiyor: "saat 14:00'te bak" diyen model 13:58'de
 * çağırdığında hata almamalı. Gerçekten GEÇMİŞ bir an (tolerans dışı) ise
 * reddediliyor — büyük ihtimalle yanlış tarih ve sessizce "şimdi"ye çevirmek
 * yanlış görevi hemen koşturmak olurdu.
 */
export function resolveOperatorDueAt(
  input: { inMinutes?: number; at?: string },
  now: Date,
): OperatorDueAtResult {
  const min = now.getTime() + OPERATOR_TASK_LIMITS.minDelayMinutes * 60_000;
  const max = now.getTime() + OPERATOR_TASK_LIMITS.maxDelayDays * 24 * 60 * 60_000;

  let target: number;
  if (input.inMinutes !== undefined) {
    target = now.getTime() + input.inMinutes * 60_000;
  } else if (input.at !== undefined) {
    target = new Date(input.at).getTime();
    if (Number.isNaN(target)) {
      return { ok: false, code: 'IN_PAST', message: 'at is not a valid ISO 8601 date-time' };
    }
    if (target < now.getTime() - OPERATOR_SCHEDULE.pastToleranceSeconds * 1000) {
      return {
        ok: false,
        code: 'IN_PAST',
        message: `at (${input.at}) is in the past — current time is ${now.toISOString()}`,
      };
    }
  } else {
    target = min;
  }

  if (target > max) {
    return {
      ok: false,
      code: 'TOO_FAR',
      message: `A task can be scheduled at most ${OPERATOR_TASK_LIMITS.maxDelayDays} days ahead`,
    };
  }
  return { ok: true, dueAt: new Date(Math.max(target, min)) };
}

/**
 * Tekrarlayan görevin sonraki vadesi. Kaçırılan pencereler TELAFİ EDİLMİYOR:
 * worker bir saat düşük kaldıysa 15 dakikalık görev dört kez art arda koşmaz,
 * bir kez koşar ve takvimine döner.
 */
export function nextOperatorDueAt(intervalMinutes: number, from: Date, now: Date): Date {
  const interval = Math.max(OPERATOR_TASK_LIMITS.minIntervalMinutes, intervalMinutes) * 60_000;
  const next = from.getTime() + interval;
  return new Date(next > now.getTime() ? next : now.getTime() + interval);
}

/**
 * Kampanya yayına alınınca OTOMATİK kurulan iki takip görevi (plan §1.C).
 *
 * `dedupeSuffix` görevin `dedupeKey`inin sonu (`launch:<ref>:<suffix>`):
 * aynı kampanya yeniden yayına alınırsa YENİ görev açılmıyor, mevcut görev
 * yeni vadeyle yeniden kuruluyor.
 *
 * Talimat İNGİLİZCE — modele gidiyor (sistem istemi ve araç açıklamaları da
 * İngilizce); kullanıcıya dönük özet koşunun kendi çıktısı.
 */
export function operatorLaunchFollowUps(campaign: {
  name: string;
  platform: string | null;
  ref: string;
  launchedAt: Date;
}): {
  dedupeSuffix: '2h' | '24h';
  title: string;
  instruction: string;
  inMinutes: number;
}[] {
  const where = `"${campaign.name}" (${campaign.platform ?? 'platform unknown'}, id ${campaign.ref})`;
  const at = campaign.launchedAt.toISOString();
  return [
    {
      dedupeSuffix: '2h',
      inMinutes: 120,
      title: OPERATOR_SYSTEM_TASK_TITLES.launchDelivery(campaign.name),
      instruction:
        `Campaign ${where} went live at ${at}. Delivery check (~2 hours after launch): ` +
        'confirm it is actually delivering — ACTIVE on the platform, impressions and spend above ' +
        'zero since launch, no disapproved or in-review-stuck ads, no billing/payment, budget, bid ' +
        'or audience-size problem blocking delivery. If it is not delivering, find the cause and ' +
        'fix it (or propose the fix when policy requires approval). Record what you found with ' +
        'remember so the 24-hour review can build on it.',
    },
    {
      dedupeSuffix: '24h',
      inMinutes: 24 * 60,
      title: OPERATOR_SYSTEM_TASK_TITLES.launchOptimization(campaign.name),
      instruction:
        `Campaign ${where} has been live for about 24 hours (since ${at}). Optimization review: ` +
        'compare spend, CTR, CPC, conversions and CPA/ROAS against its own targets, the account ' +
        'average and your notes. Pause clearly failing ads/ad sets, move budget toward winners, ' +
        'tune bids/keywords/audiences where the data justifies it. Do not over-react to ' +
        'learning-phase noise: if data is still too thin, schedule another follow-up instead of ' +
        'acting. Record the learnings with remember.',
    },
  ];
}

// ═══════════════════════════════════════════════════════════════════════════
// Hata kodları — arayüz ve sohbet aracı `code` üzerinden dallanıyor
// ═══════════════════════════════════════════════════════════════════════════

export const OperatorErrorCode = {
  NOTE_INVALID: 'OPERATOR_NOTE_INVALID',
  NOTE_NOT_FOUND: 'OPERATOR_NOTE_NOT_FOUND',
  TASK_INVALID: 'OPERATOR_TASK_INVALID',
  TASK_NOT_FOUND: 'OPERATOR_TASK_NOT_FOUND',
  /** Açık görev tavanı (`OPERATOR_TASK_LIMITS.activeMax`) dolu. */
  TASK_LIMIT: 'OPERATOR_TASK_LIMIT',
  /** Bitmiş (DONE/FAILED) görev iptal edilemez. */
  TASK_FINISHED: 'OPERATOR_TASK_FINISHED',
  /** Devriye elle iptal edilmez — ayarlardan operatör kapatılır. */
  TASK_PATROL_MANAGED: 'OPERATOR_TASK_PATROL_MANAGED',
  RUN_NOT_FOUND: 'OPERATOR_RUN_NOT_FOUND',
  /** Çalışma alanında zaten bekleyen/süren bir koşu var. */
  RUN_IN_PROGRESS: 'OPERATOR_RUN_IN_PROGRESS',
  /** Planın günlük koşu tavanı doldu. */
  DAILY_RUN_LIMIT: 'OPERATOR_DAILY_RUN_LIMIT',
  /** Abonelik yok ya da süresi dolmuş — arka plan AI'sı koşmaz. */
  PLAN_INACTIVE: 'OPERATOR_PLAN_INACTIVE',
  /**
   * AI kullanıcının yazdığı ya da iğnelediği notu arşivleyemez/yeniden
   * yazamaz (Faz 4, 29 Eyl 2026). Notu yalnız bir insan panelden kapatır.
   */
  NOTE_PROTECTED: 'OPERATOR_NOTE_PROTECTED',
  /** AI yalnız KENDİ kurduğu görevi iptal edebilir (Faz 4, 29 Eyl 2026). */
  TASK_NOT_OWNED: 'OPERATOR_TASK_NOT_OWNED',
} as const;
export type OperatorErrorCode = (typeof OperatorErrorCode)[keyof typeof OperatorErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// Koşu sonrası görev durumu — koşucu (API) ve takılı koşu kurtarması (worker)
// ═══════════════════════════════════════════════════════════════════════════

export type OperatorRunOutcome = 'SUCCEEDED' | 'FAILED';

export interface OperatorTaskAfterRun {
  status: 'PENDING' | 'DONE' | 'FAILED';
  /** Yeni vade — yalnız `PENDING`e dönen görevde. */
  dueAt: Date | null;
  failureCount: number;
  /** Devriye aralığı ayardan değiştiyse yeni değer. */
  intervalMinutes: number | null;
}

/**
 * Koşu bittiğinde görev ne olur.
 *
 *   FOLLOW_UP  başarı → DONE. Hata → `retryDelayMinutes` sonra yeniden; art
 *              arda `maxFollowUpFailures` hatada FAILED (sonsuz deneme
 *              günlük koşu tavanını yerdi).
 *   RECURRING  her durumda sonraki vade (hata tekrarlayanı öldürmez; bir
 *   / WATCH    platform kesintisi yüzünden "her sabah bak" görevi kaybolmamalı).
 *   PATROL     sonraki devriye = şimdi + çalışma alanının GÜNCEL aralığı.
 */
export function operatorTaskAfterRun(
  task: { kind: OperatorTaskKind; dueAt: Date; intervalMinutes: number | null; failureCount: number },
  outcome: OperatorRunOutcome,
  now: Date,
  patrolMinutes: number,
): OperatorTaskAfterRun {
  const failureCount = outcome === 'SUCCEEDED' ? 0 : task.failureCount + 1;

  if (task.kind === 'FOLLOW_UP') {
    if (outcome === 'SUCCEEDED') {
      return { status: 'DONE', dueAt: null, failureCount, intervalMinutes: null };
    }
    if (failureCount >= OPERATOR_SCHEDULE.maxFollowUpFailures) {
      return { status: 'FAILED', dueAt: null, failureCount, intervalMinutes: null };
    }
    return {
      status: 'PENDING',
      dueAt: new Date(now.getTime() + OPERATOR_SCHEDULE.retryDelayMinutes * 60_000),
      failureCount,
      intervalMinutes: null,
    };
  }

  if (task.kind === 'PATROL') {
    return {
      status: 'PENDING',
      dueAt: new Date(now.getTime() + patrolMinutes * 60_000),
      failureCount,
      intervalMinutes: patrolMinutes,
    };
  }

  const interval = task.intervalMinutes ?? OPERATOR_SCHEDULE.defaultWatchIntervalMinutes;
  return {
    status: 'PENDING',
    dueAt: nextOperatorDueAt(interval, task.dueAt, now),
    failureCount,
    intervalMinutes: interval,
  };
}

/**
 * Görev operatör KAPALIYKEN de koşar mı — YALNIZ bir insanın AÇIKÇA kurduğu
 * görev (`createdBy: 'user'`: panelin görev formu / `POST /operator/tasks`,
 * `automation.write` = ADMIN).
 *
 * 🔴 Faz 4 kararı (29 Eyl 2026): sohbette MODELİN kurduğu görev
 * (`createdBy: 'agent'`, `sourceConversationId` dolu) artık SAYILMIYOR. Koşu
 * öneri üretir ve otomatik pilot içinde UYGULAYABİLİR; operatörü kapatmış bir
 * çalışma alanında "yarın bak" cümlesinden doğan bir koşunun para hareket
 * ettirmesi, sahibin kapattığı özelliği modelin yeniden açması demekti. Kural
 * modelin cümleyi doğru anlayıp anlamadığına değil, kimin kurduğuna bakıyor.
 * Böyle görev operatör açılana kadar BEKLER (sohbet aracı modele bunu
 * söylüyor ki kullanıcıya dürüst olsun). Devriye ve sistem görevleri de
 * yalnız operatör açıkken koşar.
 *
 * Worker'ın claim sorgusu AYNI kuralı `where` içinde uyguluyor
 * (`operator-dispatch.service.ts`).
 */
export function operatorTaskRunsWhileDisabled(task: {
  kind: OperatorTaskKind;
  createdBy: string;
  sourceConversationId?: string | null;
}): boolean {
  if (task.kind === 'PATROL') return false;
  return task.createdBy === 'user';
}

/**
 * Devriyenin günlük koşu tavanından alabileceği en büyük pay (Faz 4 kararı).
 *
 * TRIAL'da tavan 6 ve varsayılan devriye 240 dk: günde 6 devriye tavanın
 * TAMAMINI yiyordu — olay, görev ("24 saat sonra bak") ve "şimdi bak" için
 * hiç koşu kalmıyordu. Devriye aralığı plana göre UZATILIYOR: devriye tavanın
 * en fazla yarısını kullanır. Seçenek olarak "ilk N devriye sayılmasın"
 * düşünüldü ve reddedildi — sayılmayan koşu da LLM maliyeti; tavanın amacı
 * maliyeti sınırlamak.
 */
export const OPERATOR_PATROL_CAP_SHARE = 0.5;

/**
 * Plan tavanına göre ETKİN devriye aralığı (dk). Ayar (`operatorPatrolMinutes`)
 * bir ALT sınır olarak korunur; plan daha seyrek bir ritim gerektiriyorsa o
 * kazanır. `cap` = `operatorDailyRunCap` (-1 sınırsız, 0 koşamaz → ayar aynen).
 *
 *   TRIAL 6/gün   → devriye ≤ 3/gün → ≥ 480 dk
 *   STARTER 12    → ≤ 6/gün          → ≥ 240 dk
 *   GROWTH 48     → ≤ 24/gün         → ≥ 60 dk
 *   PRO 144 / ENTERPRISE → ayar
 */
export function operatorEffectivePatrolMinutes(configuredMinutes: number, cap: number): number {
  if (cap <= 0) return configuredMinutes;
  const patrolsPerDay = Math.max(1, Math.floor(cap * OPERATOR_PATROL_CAP_SHARE));
  const floor = Math.ceil((24 * 60) / patrolsPerDay);
  return Math.min(24 * 60, Math.max(configuredMinutes, floor));
}
